import { useCallback, useMemo, useState } from 'react';

import { groupApi } from '@/api/services/groupApi';
import { userApi } from '@/api/services/userApi';
import { CheckBox } from '@/components/ui';
import type { ChinaAppSysGroup } from '@/types/entities';

import { useAdminChanges } from '../adminChanges';
import { PickerDialog, type PickerOption } from '../PickerDialog';
import { useAdminList } from './adminList';
import { AdminSubList } from './AdminSubList';

/** Gruppen eines Benutzers — Port von `IwmAdminListBenutzerGroups.razor` (REQ-28 §2). */
export interface AdminUserGroupsListProps {
  parentId: number;
}

const COLUMNS = ['Role', 'Administrative'] as const;

export function AdminUserGroupsList(props: AdminUserGroupsListProps) {
  const { parentId } = props;
  const changes = useAdminChanges();
  const load = useCallback(() => userApi.getGroups(parentId), [parentId]);
  const { rows, loading, error, reload } = useAdminList(load);
  const [options, setOptions] = useState<PickerOption[] | null>(null);
  const [busy, setBusy] = useState(false);

  const taken = useMemo(() => rows.map((group) => group.Id), [rows]);

  // Angeboten werden nur Gruppen, die der Benutzer noch nicht hat. Geladen wird erst beim
  // Öffnen — und vor dem Öffnen, damit der Dialog nicht kurz „nichts zur Auswahl" zeigt.
  const openPicker = async () => {
    setBusy(true);
    const all = await groupApi.get();
    setOptions(
      all
        .filter((group) => !taken.includes(group.Id))
        .map((group) => ({ id: group.Id, label: group.Bezeichnung ?? '' })),
    );
    setBusy(false);
  };

  const change = async (run: () => Promise<unknown>) => {
    setBusy(true);
    await run();
    await reload();
    setBusy(false);
    changes.notifyNavigationChanged();
  };

  return (
    <>
      <AdminSubList<ChinaAppSysGroup>
        title="Permissions"
        columns={COLUMNS}
        rows={rows}
        rowKey={(group) => group.Id}
        renderCells={(group) => (
          <>
            <td>{group.Bezeichnung}</td>
            <td className="iwm-admin-sublist-center">
              {/* Ohne `onChange` schreibgeschützt — geändert wird die Gruppe unter
                  „Permissions". Die Beschriftung steht nur für die Vorlesehilfe im Markup. */}
              <CheckBox
                className="iwm-admin-sublist-check"
                label={`Administrative: ${group.Bezeichnung ?? ''}`}
                checked={group.IstAdministrativ}
              />
            </td>
          </>
        )}
        loading={loading}
        error={error}
        onRetry={() => {
          void reload();
        }}
        busy={busy}
        onAdd={() => {
          void openPicker();
        }}
        onRemove={(group) => {
          void change(() => userApi.removeGroup(parentId, group.Id));
        }}
        emptyText="Keine Gruppen zugeordnet"
        errorMessage="Die Gruppen konnten nicht geladen werden."
      />
      <PickerDialog
        open={options !== null}
        title="Gruppen auswählen"
        options={options ?? []}
        emptyText="Der Benutzer ist bereits in allen Gruppen."
        onClose={() => {
          setOptions(null);
        }}
        onSubmit={async (ids) => {
          await change(async () => {
            for (const id of ids) await userApi.addGroup(parentId, id);
          });
        }}
      />
    </>
  );
}

export default AdminUserGroupsList;
